import type { Field } from '@/types/types';

/**
 * Parse field options from either array or JSON string format
 * @param field - Field object containing options
 * @returns Array of option objects with label and value
 */
export function parseFieldOptions(field: Field) {
    if (Array.isArray(field.options)) {
        return field.options;
    }
    return Object.entries(JSON.parse(<string>field.options)).map(([label, value]) => ({
        label,
        value,
    }));
}

/**
 * Parse options object/array into a consistent [{label, value}] format
 * Handles JSON string, {Label: value} object format and [{label, value}] array format
 * @param options - Options in either format
 * @returns Array of option objects with label and value
 */
export function parseOptions(
    options: Record<string, string> | Array<{ label: string; value: string }> | string | null | undefined,
): Array<{ label: string; value: string }> {
    if (!options) return [];

    // If it's a JSON string, parse it first
    if (typeof options === 'string') {
        try {
            options = JSON.parse(options);
        } catch {
            return [];
        }
    }

    // Already in array format [{label, value}]
    if (Array.isArray(options)) {
        return options.filter((opt) => opt.label && opt.value);
    }

    // Object format {Label: value}
    return Object.entries(options)
        .filter(([label, value]) => label && value && typeof value === 'string')
        .map(([label, value]) => ({ label, value }));
}
